<template>
  <div v-loading="isLoading">
    <div class="p-x2" v-if="isLoading"></div>
    <div class="dialog-content" v-if="!isLoading">
      <div class="caption">基本情報</div>
      <div class="message message-error" v-if="!existAccounts && mode !== 'edit'">
        キャンペーン実施アカウントが登録されていません。<br />グループ設定画面から追加してください。
      </div>
      <div v-else>
        <!-- 実施アカウント-->
        <div class="input-container">
          <div class="label">実施アカウント</div>
          <div class="input">
            <account-select
              v-if="account.id"
              :accounts="accounts"
              :onChange="onChangeCampaignAccount"
              :disabled="mode === 'edit'"
              :selected-account-id="account.id"
            ></account-select>
            <div class="message message-error" v-else>キャンペーン実施アカウントが削除されました。</div>
          </div>
          <div class="mt-x1 message message-error" v-if="account && account.isTokenExpired === 1">
            アクセストークンの有効期限が切れています。<br />グループ設定画面から更新してください。
          </div>
          <div v-if="mode === 'edit' && account.id">
            <div class="message message-info mt-x1">
              実施アカウントは変更できません。変更したい場合、キャンペーンを削除して再度作成してください。
            </div>
          </div>
        </div>
        <!-- キャンペーンタイトル-->
        <div class="input-container">
          <div class="label">キャンペーンタイトル</div>
          <div class="input">
            <el-input
              placeholder="キャンペーンタイトル"
              :maxlength="50"
              :class="{ success: $v.campaign.title.required && !$v.campaign.title.$invalid }"
              type="text"
              v-model="campaign.title"
              @input="checkFormIsValid"
              tabindex="1"
              :disabled="canNotEditInstantwin"
              @change="onChangeCampaignTitle"
            ></el-input>
            <div class="input-invalid-text" v-if="!$v.campaign.title.required && $v.campaign.title.$error">
              キャンペーンタイトルを入力してください。
            </div>
          </div>
        </div>
        <!-- キャンペーン期間-->
        <div class="input-container">
          <div class="label">キャンペーン期間</div>
          <!-- キャンペーン開始日時-->
          <div class="input mb-x2">
            <el-date-picker
              placeholder="開始日"
              type="date"
              value-format="yyyy-MM-dd"
              :picker-options="datePickerOption"
              prefix-icon="mdi mdi-calendar-today"
              :editable="false"
              :disabled="isFinished || isStarting || canNotEditInstantwin"
              v-model="startDate"
              @change="onChangeStartDate"
              ref="startDate"
              tabindex="2"
            ></el-date-picker>
            <el-time-select
              class="ml-x2"
              placeholder="開始時間"
              :picker-options="timePickerOption"
              prefix-icon="mdi mdi-clock"
              :editable="false"
              :disabled="isFinished || isStarting || canNotEditInstantwin"
              v-model="startTime"
              @change="onChangeStartTime"
              ref="startTime"
              tabindex="3"
            ></el-time-select
            ><span class="ml-x2">以降</span>
            <div class="input-invalid-text" v-if="startDatetimeInvalid">
              キャンペーン開始日時は、現在日時より後を指定してください。
            </div>
          </div>
          <!-- キャンペーン終了日時-->
          <div class="input">
            <el-date-picker
              placeholder="終了日"
              type="date"
              value-format="yyyy-MM-dd"
              :picker-options="datePickerOption"
              prefix-icon="mdi mdi-calendar-today"
              :editable="false"
              :disabled="isFinished || canNotEditInstantwin"
              v-model="endDate"
              @change="onChangeEndDate"
              ref="endDate"
              tabindex="4"
            ></el-date-picker>
            <el-time-select
              class="ml-x2"
              placeholder="終了時間"
              :picker-options="timePickerOption"
              prefix-icon="mdi mdi-clock"
              :editable="false"
              :disabled="isFinished || canNotEditInstantwin"
              v-model="endTime"
              @change="onChangeEndTime"
              ref="endTime"
              tabindex="5"
            ></el-time-select
            ><span class="ml-x2">未満</span>
            <div class="input-invalid-text" v-if="endDatetimeInvalid">
              キャンペーン終了日時は、開始日時より後を指定してください。
            </div>
            <div class="input-invalid-text" v-if="isCampaignPeriodEmpty">キャンペーン期間を入力してください。</div>
          </div>
        </div>
        <div class="input-container">
          <div class="label">
            同一応募者への1日の最大返信数
            <el-tooltip placement="top">
              <div slot="content">
                同一のアカウントから複数回の応募があった場合に、返信する回数です。<br />2件以上に設定した場合、同一内容ポストエラー防止のため、返信内容にURLを含める必要があります。
              </div>
              <i class="mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <el-input-number
            controls-position="right"
            :min="1"
            :max="20"
            v-model="campaign.dailySendLimitCount"
            :disabled="mode === 'edit' || canNotEditInstantwin"
            tabindex="6"
            @change="onChangeDailySendLimitCount"
          ></el-input-number>
        </div>
        <!-- 応募条件-->
        <div class="caption">応募条件</div>
        <!-- アクション種類-->
        <div class="input-container">
          <div class="label">
            アクション種類
            <el-tooltip placement="top">
              <div slot="content">キャンペーン応募の条件となるアクションを指定してください。</div>
              <i class="mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <el-select v-model="campaign.entryActionType" @change="onChangeEntryActionType">
            <el-option
              v-for="(option, index) in options"
              :key="index"
              :label="option.label"
              :value="option.value"
              :disabled="isFinished || canNotEditInstantwin"
            ></el-option>
          </el-select>
          <div class="message-text" v-if="campaign.entryActionType === 'retweet'">
            キャンペーンキーワードを含む実施アカウントのポストをリポスト
          </div>
          <div class="message-text" v-if="campaign.entryActionType === 'quote_tweet'">
            キャンペーンキーワードを含む実施アカウントのポストを引用
          </div>
          <div class="message-text" v-if="campaign.entryActionType === 'retweet_and_quote_tweet'">
            キャンペーンキーワードを含む実施アカウントのポストをリポスト、引用
          </div>
          <div class="message-text" v-if="campaign.entryActionType === 'mention'">
            キャンペーンキーワード、実施アカウントへのメンションを含むポスト<br />返信（リプライ）は収集できません
          </div>
          <div
            class="input-invalid-text"
            v-if="!$v.campaign.entryActionType.required && $v.campaign.entryActionType.$error && formValid === false"
          >
            アクション種類を選択してください。
          </div>
        </div>
        <!-- キャンペーンキーワード-->
        <div class="input-container">
          <div class="label">
            キャンペーンキーワード
            <el-tooltip placement="top">
              <div slot="content">
                キャンペーンポストに含まれる、キャンペーンを識別するためのキーワードを入力してください。複数入力すると全てのキーワードを満たしたポストが対象になります。
              </div>
              <i class="mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <div class="input">
            <tag-input
              :tags="campaign.entryKeyword"
              add-tag-text="キーワード追加"
              :onChange="onChangeEntryKeyword"
              :disabled="isFinished || canNotEditInstantwin"
              tabindex="10"
            ></tag-input>
          </div>
          <div class="input-invalid-text" v-if="formValid === false && campaign.entryKeyword.length === 0">
            キャンペーンキーワードを追加してください。
          </div>
        </div>
        <!-- メディア付きポスト-->
        <div class="input-container">
          <el-switch
            v-model="campaign.isRequireMedia"
            :disabled="isDisabledRequireMedia || canNotEditInstantwin"
            tabindex="11"
            active-text="メディア付きポスト"
            @change="onChangeRequireMedia"
          ></el-switch>
          <el-tooltip placement="top">
            <div slot="content">応募ポストにメディアが含まれる場合のみ、応募と判定します。</div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
        </div>
        <!-- 応募者フィルタ-->
        <div class="caption">応募者フィルタ</div>
        <!-- 実施アカウントをフォローしていない場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.requireFollow"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="12"
            active-text="実施アカウントをフォローしていない場合、落選にする"
            @change="onChangeRequireFollow"
          ></el-switch>
        </div>
        <!-- 当キャンペーンで当選実績がある場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeMultiWinner"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="13"
            active-text="当キャンペーンで当選実績がある場合、落選にする"
            @change="onChangeExcludeMultiWinner"
          ></el-switch>
          <el-tooltip placement="top">
            <div slot="content">
              キャンペーン内ですでに当選している場合、落選となります。<br />OFFの場合、重複当選を許可します。
            </div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
        </div>
        <!-- 別キャンペーンで当選実績がある場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeOtherCampaignWinner"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="14"
            active-text="別キャンペーンで当選実績がある場合、落選にする(インスタントウィンのみ)"
            @change="onChangeExcludeOtherCampaignWinner"
          ></el-switch>
          <el-tooltip placement="top">
            <div slot="content">グループ内の別キャンペーンで、1回以上当選している場合、落選となります。</div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
        </div>
        <!-- 懸賞アカウントの場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeHunterAccount"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="15"
            active-text="懸賞アカウントの場合、落選にする"
            @change="onChangeExcludeHunterAccount"
          ></el-switch>
          <el-tooltip placement="top">
            <div slot="content">懸賞が主目的のアカウントを自動判定し、該当する場合は落選とします。</div>
            <i class="mdi mdi-help-circle"></i>
          </el-tooltip>
        </div>
        <!-- アカウント開設日がキャンペーン期間内の場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeAccountCreateDateWithinPeriod"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="16"
            active-text="アカウント開設日がキャンペーン期間内の場合、落選にする"
            @change="onChangeExcludeAccountCreateDateWithinPeriod"
          ></el-switch>
        </div>
        <!-- プロフィール画像が初期状態の場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeDefaultProfileImage"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="17"
            active-text="プロフィール画像が初期状態の場合、落選にする"
            @change="onChangeExcludeDefaultProfileImage"
          ></el-switch>
        </div>
        <!-- 自己紹介が未入力の場合、落選にする-->
        <div class="input-container">
          <el-switch
            v-model="campaign.excludeNoDescription"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="18"
            active-text="自己紹介が未入力の場合、落選にする"
            @change="onChangeExcludeNoDescription"
          ></el-switch>
        </div>
        <!-- フォロワー数が指定値未満の場合、落選にする(0=制限なし)-->
        <div class="input-container">
          <div class="label">フォロワー数が指定値未満の場合、落選にする(0=制限なし)</div>
          <el-input-number
            controls-position="right"
            :min="0"
            v-model="campaign.minFollowers"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="19"
            @change="onChangeMinFollowers"
          ></el-input-number>
        </div>
        <!-- ポスト数が指定値未満の場合、落選にする(0=制限なし)-->
        <div class="input-container">
          <div class="label">ポスト数が指定値未満の場合、落選にする(0=制限なし)</div>
          <el-input-number
            controls-position="right"
            :min="0"
            v-model="campaign.minTweets"
            :disabled="isFinished || canNotEditInstantwin"
            tabindex="20"
            @change="onChangeMinTweets"
          ></el-input-number>
        </div>
        <!-- 自己紹介に次のキーワードを含む場合、落選にする(「,」区切りで一括入力可)-->
        <div class="input-container">
          <div class="label">自己紹介に次のキーワードを含む場合、落選にする(「,」区切りで一括入力可)</div>
          <div class="input">
            <tag-input
              :tags="campaign.excludeDescriptionKeyword"
              add-tag-text="キーワード追加"
              :onChange="onChangeExcludeDescriptionKeyword"
              :disabled="isFinished || canNotEditInstantwin"
              tabindex="21"
            ></tag-input>
          </div>
        </div>
      </div>
    </div>
    <div class="dialog-footer" v-if="!isLoading" slot="footer">
      <div v-if="!existAccounts && mode !== 'edit'">
        <el-button type="primary" @click="$router.push('/settings/group')"
          >グループ設定へ<i class="mdi mdi-arrow-right ml-x1"></i
        ></el-button>
      </div>
      <div v-else>
        <el-button
          v-if="mode === 'add'"
          type="primary"
          @click="submit()"
          :disabled="
            $v.$invalid === true ||
            formValid === false ||
            !account ||
            !account.id ||
            account.isTokenExpired === 1 ||
            canNotEditInstantwin
          "
          ><i class="mdi mdi-content-save"></i>新規作成</el-button
        >
        <div v-if="mode === 'edit'" layout layout-align="space-between">
          <el-button type="default" @click="deleteCampaign()" :disabled="canNotEditInstantwin"
            ><i class="mdi mdi-delete-forever"></i>削除</el-button
          >
          <el-button
            type="primary"
            @click="submit()"
            :disabled="
              $v.$invalid === true ||
              formValid === false ||
              !account ||
              !account.id ||
              account.isTokenExpired === 1 ||
              canNotEditInstantwin
            "
            ><i class="mdi mdi-content-save"></i>保存</el-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.input-container {
  width: 100%;
  margin-bottom: $spacing-unit * 2;

  .label {
    margin-bottom: $spacing-unit;
  }

  .mdi-help-circle {
    margin-left: $spacing-unit;
    font-size: 20px;
  }

  .el-input-number {
    width: $spacing-unit * 18;
  }

  .message-text {
    font-size: $font-small-size;
    color: $sub-text-color;
  }
}

.caption {
  margin: $spacing-unit * 4 0 $spacing-unit * 2 0;

  &:first-child {
    margin-top: 0;
  }
}

.message-action-title {
  margin-top: $spacing-unit;
  font-weight: bold;

  &:first-child {
    margin-top: 0;
  }
}

.dialog-footer {
  text-align: right;
}

:deep(.el-select .el-input--suffix) {
  width: 250px;
  margin-bottom: $spacing-unit;
}
</style>
<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import 'moment-timezone'
import moment from 'moment'
import { mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import AccountSelect from '@/client/components/_atoms/account_select'
import TagInput from '@/client/components/_atoms/tag_input'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useSystemStore } from '@/client/store/useSystemStore'
import { validateSingleKeyword } from '@/client/utils/validation'

export default defineComponent({
  components: {
    AccountSelect,
    TagInput
  },
  props: {
    mode: String,
    campaignId: Number,
    isCopy: Boolean,
    isChangeWithoutSubmit: Boolean
  },
  data() {
    return {
      isLoading: true,
      isSubmit: false,
      formValid: false,
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      startDatetimeInvalid: false,
      endDatetimeInvalid: false,
      accounts: [],
      account: {
        id: null
      },
      campaign: {
        title: '',
        startDatetime: '',
        status: CAMPAIGN_STATUS.BEFORE_EVENT,
        endDatetime: '',
        entryKeyword: [],
        entryActionType: 'retweet',
        requireFollow: true,
        excludeHunterAccount: true,
        excludeMultiWinner: true,
        excludeOtherCampaignWinner: true,
        excludeAccountCreateDateWithinPeriod: true,
        excludeDefaultProfileImage: true,
        excludeNoDescription: true,
        minFollowers: 0,
        dailySendLimitCount: 10,
        oldDailySendLimitCount: 10,
        minTweets: 0,
        excludeDescriptionKeyword: [],
        isRequireMedia: false
      },
      rawCampaignData: {},
      options: [
        { label: '公式リポスト', value: 'retweet' },
        { label: '引用', value: 'quote_tweet' },
        { label: '公式リポスト & 引用', value: 'retweet_and_quote_tweet' },
        { label: '実施アカウントへのメンション', value: 'mention' }
      ],
      datePickerOption: {
        // 開始日の過去日禁止（selectedDate < today）と cutoff（>= 2025-11-01）の両方を撤廃
        // （Story #1170 / requirements A1-004）。過去日を含めて開始日を選択可能とする。
        disabledDate() {
          return false
        }
      },
      timePickerOption: {
        start: '00:00',
        step: '01:00',
        end: '23:00'
      }
    }
  },

  mounted() {
    this.init()
  },

  watch: {
    campaignId: function () {
      this.init()
    },
    'campaign.entryActionType'() {
      if (this.campaign.entryActionType === 'retweet' || this.campaign.entryActionType === 'retweet_and_quote_tweet') {
        this.campaign.isRequireMedia = false
      }
    },
    isFormChanged(value: boolean) {
      this.$emit('update:isChangeWithoutSubmit', value)
    }
  },

  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup.id,
      allowCreateInstantwin: state => Boolean(state.currentGroup.allowCreateInstantwin),
      isSystemAdmin: state => Boolean(state.user.isSystemAdmin)
    }),
    isBeforeStart() {
      return this.campaign.status === CAMPAIGN_STATUS.BEFORE_EVENT
    },
    isStarting() {
      return [CAMPAIGN_STATUS.BEFORE_COLLECTION, CAMPAIGN_STATUS.DURING_COLLECTION].includes(this.campaign.status)
    },
    isFinished() {
      return [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
        this.campaign.status
      )
    },
    isDisabledRequireMedia() {
      return this.campaign.entryActionType === 'retweet' || this.campaign.entryActionType === 'retweet_and_quote_tweet'
    },
    isFormChanged() {
      return JSON.stringify(this.campaignData) !== JSON.stringify(this.rawCampaignData)
    },
    isCampaignPeriodEmpty() {
      return !this.startDate || !this.startTime || !this.endDate || !this.endTime
    },
    campaignData() {
      const startDatetimeString = this.startTime ? this.startDate + ' ' + this.startTime : this.startDate
      const endDatetimeString = this.endTime ? this.endDate + ' ' + this.endTime : this.endDate

      const startDatetime = moment(startDatetimeString).format()
      const endDatetime = moment(endDatetimeString).format()

      const campaignData = this.setCampaignData(startDatetime, endDatetime)

      return campaignData
    },
    existAccounts() {
      return this.accounts.length > 0
    },
    canNotEditInstantwin() {
      return !this.isSystemAdmin && !this.allowCreateInstantwin
    }
  },

  methods: {
    /**
     * 初期化
     */
    async init() {
      this.account = {
        id: null
      }
      this.isLoading = true
      this.accounts = []
      this.isSubmit = false
      this.$v.$reset()
      if (!this.campaignId) {
        this.formValid = false
        this.datetimeValid = false
        this.startDatetimeInvalid = false
        this.endDatetimeInvalid = false
        this.startDate = ''
        this.startTime = ''
        this.endDate = ''
        this.endTime = ''
        this.campaign = {
          title: '',
          status: CAMPAIGN_STATUS.BEFORE_EVENT,
          startDatetime: '',
          endDatetime: '',
          entryKeyword: [],
          entryActionType: 'retweet',
          requireFollow: true,
          excludeHunterAccount: true,
          excludeMultiWinner: true,
          excludeOtherCampaignWinner: true,
          excludeAccountCreateDateWithinPeriod: true,
          excludeDefaultProfileImage: true,
          excludeNoDescription: true,
          minFollowers: 0,
          oldMinFollowers: 0,
          dailySendLimitCount: 10,
          oldDailySendLimitCount: 10,
          minTweets: 0,
          oldMinTweets: 0,
          excludeDescriptionKeyword: [],
          isRequireMedia: false
        }
      } else {
        if (!this.isCopy) {
          this.formValid = true
          this.datetimeValid = true
        }
        this.startDatetimeInvalid = false
        this.endDatetimeInvalid = false
        // キャンペーンデータを取得
        await this.getCampaign()

        if (!this.isCopy) {
          this.startDate = moment(this.campaign.startDatetime).format('YYYY-MM-DD')
          this.startTime = moment(this.campaign.startDatetime).format('HH:mm')
          this.endDate = moment(this.campaign.endDatetime).format('YYYY-MM-DD')
          this.endTime = moment(this.campaign.endDatetime).format('HH:mm')
        }
      }
      // キャンアカウント一覧を取得
      await this.getCampaignAccounts()

      const startDatetime = moment(this.campaign.startDatetime).format()
      const endDatetime = moment(this.campaign.endDatetime).format()

      this.rawCampaignData = this.setCampaignData(startDatetime, endDatetime)
      this.isLoading = false
    },

    /**
     * Set campaign data
     * @param {string} startDatetime
     * @param {string} endDatetime
     * @returns {Object} campaign data
     */
    setCampaignData(startDatetime: string, endDatetime: string) {
      return {
        snsType: this.account.snsType,
        groupId: this.currentGroupId,
        campaign: {
          title: this.campaign.title,
          startDatetime,
          endDatetime,
          type: 'instantwin',
          setting: {
            entryKeyword: JSON.stringify(this.campaign.entryKeyword),
            entryActionType: this.campaign.entryActionType,
            requireFollow: this.campaign.requireFollow ? 1 : 0,
            excludeHunterAccount: this.campaign.excludeHunterAccount ? 1 : 0,
            excludeMultiWinner: this.campaign.excludeMultiWinner ? 1 : 0,
            excludeOtherCampaignWinner: this.campaign.excludeOtherCampaignWinner ? 1 : 0,
            excludeAccountCreateDateWithinPeriod: this.campaign.excludeAccountCreateDateWithinPeriod ? 1 : 0,
            excludeDefaultProfileImage: this.campaign.excludeDefaultProfileImage ? 1 : 0,
            excludeNoDescription: this.campaign.excludeNoDescription ? 1 : 0,
            minFollowers: this.campaign.minFollowers,
            oldMinFollowers: this.campaign.minFollowers,
            dailySendLimitCount: this.campaign.dailySendLimitCount,
            minTweets: this.campaign.minTweets,
            oldMinTweets: this.campaign.minTweets,
            excludeDescriptionKeyword:
              this.campaign.excludeDescriptionKeyword && this.campaign.excludeDescriptionKeyword.length > 0
                ? JSON.stringify(this.campaign.excludeDescriptionKeyword)
                : null,
            isRequireMedia: this.campaign.isRequireMedia ? 1 : 0
          }
        },
        account: {
          id: this.account.id
        }
      }
    },

    /**
     * キャンアカウント一覧を取得
     */
    async getCampaignAccounts() {
      const params = {
        groupId: this.currentGroupId
      }
      const result = await api.get('/twitter_campaign_accounts', {
        params
      })
      if (!result.data || !result.data.accounts.length) {
        return
      }
      this.accounts = result.data.accounts
      if (!this.accounts.length) {
        this.account = {
          id: null
        }
        return
      }
      if (this.mode === 'add' && !this.isCopy) {
        this.account = this.accounts[0]
      } else if (this.mode === 'edit') {
        if (!this.account) {
          this.account = {
            id: null
          }
        }
        const account = this.accounts.find(account => {
          return account.id === this.account.id
        })
        if (account) {
          this.account = account
        } else {
          this.account = {
            id: null
          }
        }
      }
    },

    /**
     * キャンペーンデータを取得
     */
    async getCampaign() {
      if (!this.campaignId) {
        return
      }
      const result = await api.get('/campaigns/' + this.campaignId)
      if (!result || !result.data) {
        this.$notify({
          title: 'キャンペーンの取得に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        this.$emit('actionSuccess')
        return
      }

      const campaign = result.data

      this.campaign = {
        title: campaign.title,
        startDatetime: !this.isCopy ? campaign.startDatetime : '',
        endDatetime: !this.isCopy ? campaign.endDatetime : '',
        entryActionType: campaign.setting.entryActionType,
        entryKeyword: JSON.parse(campaign.setting.entryKeyword),
        excludeDescriptionKeyword: campaign.setting.excludeDescriptionKeyword
          ? JSON.parse(campaign.setting.excludeDescriptionKeyword)
          : [],
        requireFollow: Boolean(campaign.setting.requireFollow),
        excludeHunterAccount: Boolean(campaign.setting.excludeHunterAccount),
        excludeMultiWinner: Boolean(campaign.setting.excludeMultiWinner),
        excludeOtherCampaignWinner: Boolean(campaign.setting.excludeOtherCampaignWinner),
        excludeAccountCreateDateWithinPeriod: Boolean(campaign.setting.excludeAccountCreateDateWithinPeriod),
        excludeDefaultProfileImage: Boolean(campaign.setting.excludeDefaultProfileImage),
        excludeNoDescription: Boolean(campaign.setting.excludeNoDescription),
        minFollowers: campaign.setting.minFollowers,
        oldMinFollowers: campaign.setting.minFollowers,
        dailySendLimitCount: campaign.setting.dailySendLimitCount,
        minTweets: campaign.setting.minTweets,
        oldMinTweets: campaign.setting.minTweets,
        status: !this.isCopy ? campaign.status : CAMPAIGN_STATUS.BEFORE_EVENT,
        isRequireMedia: Boolean(campaign.setting.isRequireMedia)
      }

      if (this.isDisabledRequireMedia) {
        this.campaign.isRequireMedia = false
      }

      if (campaign.account) {
        this.account = campaign.account

        if (this.isCopy) {
          this.account.snsType = campaign.snsType
        }
      }
    },

    /**
     * フォーム検証チェック
     */
    checkFormIsValid() {
      this.$v.$touch()
      // キャンペーン期間検証チェック
      this.checkDatetimeIsValid()
      if (!this.datetimeValid) {
        this.formValid = false
        return
      }
      if (!this.campaign.title) {
        this.formValid = false
        return
      }
      if (!this.campaign.entryKeyword.length) {
        this.formValid = false
        return
      }
      this.formValid = true
    },

    /**
     * キャンペーン期間検証チェック
     */
    checkDatetimeIsValid() {
      // 開始日時チェック
      if (this.isBeforeStart && this.startDate && this.startTime) {
        const startDatetime = this.startDate + ' ' + this.startTime
        if (startDatetime < moment().format('YYYY-MM-DD HH:mm')) {
          this.startDatetimeInvalid = true
          this.datetimeValid = false
          return
        } else {
          this.startDatetimeInvalid = false
        }
      }

      // 期間チェック
      if (this.startDate && this.startTime && this.endDate && this.endTime) {
        const startDatetime = this.startDate + ' ' + this.startTime
        const endDatetime = this.endDate + ' ' + this.endTime
        if (startDatetime >= endDatetime) {
          this.datetimeValid = false
          this.endDatetimeInvalid = true
        } else {
          this.datetimeValid = true
          this.endDatetimeInvalid = false
        }
      } else {
        this.datetimeValid = false
      }
    },

    /**
     * エントリの変更キーワード
     * @param {string[]} keywords
     */
    onChangeEntryKeyword(keywords: string[]) {
      if (this.campaign.entryKeyword.length < keywords.length) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|キャンペーン|キーワード追加')
      } else TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|キャンペーン|削除')

      const origin = Object.assign([], this.campaign.entryKeyword)
      keywords = this.formatKeyword(keywords)
      if (this.isDuplicated(keywords, origin)) {
        this.campaign.entryKeyword = Object.assign([], origin)
        return
      }
      this.campaign.entryKeyword = keywords
      if (this.isKeywordsOver(keywords)) {
        const message = 'キャンペーンキーワードの合計を100文字以下にしてください。'
        this.campaign.entryKeyword = Object.assign([], origin)
        this.showKeywordsOverNotify(message)
      }
      if (!this.isKeywordCorrect(keywords)) {
        const message = 'キーワードに利用できない文字列が含まれています。'
        this.campaign.entryKeyword = Object.assign([], origin)
        this.showKeywordsOverNotify(message)
      }
      this.checkFormIsValid()
    },

    /**
     * 説明キーワードを除外
     * @param {string[]} keywords
     */
    onChangeExcludeDescriptionKeyword(keywords: string[]) {
      if (this.campaign.excludeDescriptionKeyword.length < keywords.length) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|キーワード含む場合、落選|追加')
      } else TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|キーワード含む場合、落選|削除')

      const origin = [...this.campaign.excludeDescriptionKeyword]

      const uniqueKeywords = Array.from(
        new Set(
          keywords.flatMap(v =>
            v
              .split(',')
              .map(v => v.trim())
              .filter(Boolean)
          )
        )
      )

      this.campaign.excludeDescriptionKeyword = uniqueKeywords
      if (this.isKeywordsOver(uniqueKeywords)) {
        const message = '自己紹介に次のキーワード合計を100文字以下にしてください。'
        this.campaign.excludeDescriptionKeyword = origin
        this.showKeywordsOverNotify(message)
      }
      this.checkFormIsValid()
    },

    /**
     * キーワード数が100文字を超えるチェック
     */
    isKeywordsOver(keywords: string[]) {
      if (keywords.join('').length > 100) {
        return true
      }
      return false
    },

    /**
     * Check input hashtag is duplicate
     * @param {string[]} keywords
     * @param {string[]} origin
     * @returns {boolean} is duplicate or not
     */
    isDuplicated(keywords: string[], origin: string[]): boolean {
      // If action is delete hashtag
      if (origin.length > keywords.length) {
        return false
      }
      // Get new hashtag
      const latestInput = keywords[keywords.length - 1]
      if (origin.includes(latestInput)) {
        return true
      }
      return false
    },

    /**
     * Reformat keyword
     * Remove space in head and tail
     * Add # to head
     * @param {string[]} keywords
     * @returns {string[]} new keywords
     */
    formatKeyword(keywords: string[]): string[] {
      const newKeywords = []

      for (let keyword of keywords) {
        keyword = keyword.trim()

        newKeywords.push(keyword)
      }
      return newKeywords
    },

    /**
     * Check if keyword is correct
     * @param {string[]} keywords
     * @returns {boolean} correct
     */
    isKeywordCorrect(keywords: string[]): boolean {
      for (const keyword of keywords) {
        const result = validateSingleKeyword(keyword)

        if (!result.isValid) {
          return false
        }
      }
      return true
    },

    /**
     * キーワード数が100文字を超える通知を表示
     */
    showKeywordsOverNotify(message: string) {
      this.$notify({
        title: message,
        customClass: 'danger',
        duration: 5000
      })
    },

    submit() {
      // validations
      this.$v.$touch()
      if (this.$v.$invalid === true) {
        return
      }
      if (!this.startDate) {
        this.$refs.startDate.focus()
        return
      }
      if (!this.startTime) {
        this.$refs.startTime.focus()
        return
      }
      if (!this.endDate) {
        this.$refs.endDate.focus()
        return
      }
      if (!this.endTime) {
        this.$refs.endTime.focus()
        return
      }
      this.checkFormIsValid()
      if (this.formValid === false) {
        return
      }
      if (this.isSubmit) {
        return
      }

      if (this.mode === 'add') {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|新規作成')

        this.createCampaign()
      } else if (this.mode === 'edit') {
        this.updateCampaign()
      }
    },

    /**
     * キャンペーンの新規作成
     */
    async createCampaign() {
      this.isSubmit = true

      // キャンペーンを保存する
      const result = await api.post('/campaigns', this.campaignData)

      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'MAX_CAMPAIGN_COUNT_OVER') {
        this.$notify({
          title: '同時開催可能件数を超えるため、指定の期間ではキャンペーンを開催できません。',
          customClass: 'danger',
          duration: 5000
        })

        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'キャンペーンを作成しました。',
        customClass: 'success',
        duration: 5000
      })
      // キャンペーン一覧を取得
      this.$emit('actionSuccess')
    },

    /**
     * キャンペーンの変更
     */
    async updateCampaign() {
      this.isSubmit = true

      // キャンペーンを保存する
      const result = await api.put('/campaigns/' + this.campaignId, this.campaignData)
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: 'キャンペーン実施アカウントが登録されていません。',
          customClass: 'danger',
          duration: 5000
        })
        // キャンペーン一覧を取得
        this.$emit('actionSuccess')
        return
      }

      if (result.error && result.error.type === 'MAX_CAMPAIGN_COUNT_OVER') {
        this.$notify({
          title: '同時開催可能件数を超えるため、指定の期間ではキャンペーンを開催できません。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }
      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: 'キャンペーンの保存に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'キャンペーンを保存しました。',
        customClass: 'success',
        duration: 5000
      })

      // キャンペーン一覧を取得
      this.$emit('actionSuccess')
    },

    /**
     * キャンペーンを削除
     */
    async deleteCampaign() {
      const confirm = window.confirm('キャンペーンを削除してよろしいですか？')
      if (!confirm) {
        return
      }
      this.isSubmit = true
      // キャンペーンを保存する
      const result = await api.delete('/campaigns/' + this.campaignId)
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のキャンペーンはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        this.$emit('actionSuccess')
        return
      }
      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: 'キャンペーンの削除に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'キャンペーンを削除しました。',
        customClass: 'success',
        duration: 5000
      })

      // キャンペーン一覧を取得
      this.$emit('actionSuccess')
    },

    /**
     * キャンペーンアカウント変更イベント
     */
    onChangeCampaignAccount(accountId: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|実施アカウント')

      this.account = this.accounts.find(account => {
        return account.id === accountId
      })
    },

    onChangeCampaignTitle() {
      TrackingService.sendEvent('input:キャンペーンの新規作成(IW)|キャンペーンタイトル')
    },

    onChangeStartDate() {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|開始日')

      this.checkFormIsValid()
    },

    onChangeStartTime() {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|開始時間')

      this.checkFormIsValid()
    },

    onChangeEndDate() {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|終了日')

      this.checkFormIsValid()
    },

    onChangeEndTime() {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|終了時間')

      this.checkFormIsValid()
    },

    onChangeDailySendLimitCount() {
      if (this.campaign.dailySendLimitCount === this.campaign.oldDailySendLimitCount + 1) {
        TrackingService.sendEvent('input:キャンペーン新規作成(IW)|同一応募者1日の最大返信数|UP')
      } else if (this.campaign.dailySendLimitCount === this.campaign.oldDailySendLimitCount - 1) {
        TrackingService.sendEvent('input:キャンペーン新規作成(IW)|同一応募者1日の最大返信数|DOWN')
      } else TrackingService.sendEvent('input:キャンペーン新規作成(IW)|同一応募者1日の最大返信数')

      this.campaign.oldDailySendLimitCount = this.campaign.dailySendLimitCount
    },

    onChangeEntryActionType() {
      TrackingService.sendEvent('select:キャンペーンの新規作成(IW)|アクション種類')
    },

    onChangeRequireMedia() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|メディア付きポスト')
    },

    onChangeRequireFollow() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|フォローしていない場合、落選')
    },

    onChangeExcludeMultiWinner() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|当選実績がある場合、落選')
    },

    onChangeExcludeOtherCampaignWinner() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|当選実績がある場合、落選(IW)')
    },

    onChangeExcludeHunterAccount() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|懸賞アカウントの場合、落選にする')
    },

    onChangeExcludeAccountCreateDateWithinPeriod() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|開設日が期間内の場合、落選')
    },

    onChangeExcludeDefaultProfileImage() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|初期状態の場合、落選にする')
    },

    onChangeExcludeNoDescription() {
      TrackingService.sendEvent('switch:キャンペーンの新規作成(IW)|自己紹介が未入力の場合、落選')
    },

    onChangeMinFollowers() {
      if (this.campaign.minFollowers === this.campaign.oldMinFollowers + 1) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|フォロワーが未満で落選|UP')
      } else if (this.campaign.minFollowers === this.campaign.oldMinFollowers - 1) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|フォロワーが未満で落選|DOWN')
      } else TrackingService.sendEvent('input:キャンペーンの新規作成(IW)|フォロワーが未満で落選')

      this.campaign.oldMinFollowers = this.campaign.minFollowers
    },

    onChangeMinTweets() {
      if (this.campaign.minTweets === this.campaign.oldMinTweets + 1) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|ポストが未満で落選|UP')
      } else if (this.campaign.minTweets === this.campaign.oldMinTweets - 1) {
        TrackingService.sendEvent('click:キャンペーンの新規作成(IW)|ポストが未満で落選|DOWN')
      } else TrackingService.sendEvent('input:キャンペーンの新規作成(IW)|ポストが未満で落選')

      this.campaign.oldMinTweets = this.campaign.minTweets
    }
  },

  validations: {
    campaign: {
      title: {
        required
      },
      entryActionType: {
        required
      }
    }
  }
})
</script>
